import React, { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../../Components/TextInput/TextInputFieldC";
import Select from "../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import { IPrinter, IPrinterOption } from "./IPrinter";
import { printerCreate_Update } from "../../../Redux/Slices/Booklet/Admin/PrinterMaster/PrinterMasterCreate_Update";
import { PrinterOptions } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/PrinterOptions";

interface PrinterFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<IPrinter>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<IPrinter>>>;
}

const printerSchema = z.object({
  printer_id: z.string().default("0"),
  printer_name: z.string().min(1, { message: "Printer name is required" }),
  model: z.string().min(1, { message: "Model is required" }),
  status: z.boolean(),
  papper_size: z.string().min(1, { message: "Paper size is required" }),
  side: z.string().min(1, { message: "Side is required" }),
  page_orientation: z
    .string()
    .min(1, { message: "Page orientation is required" }),
  scale: z.string().min(1, { message: "Scale is required" }),
  stapler: z.boolean().default(false),
  stapler_position: z
    .string()
    .min(1, { message: "Stapler position is required" }),
  color: z.string().min(1, { message: "Color is required" }),
  pages_per_sheet: z
    .string()
    .min(1, { message: "Pages per sheet is required" }),
  is_delete: z.boolean().default(false),
});

const PrinterForm: FunctionComponent<PrinterFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
}) => {
  const dispatch = useAppDispatch();

  const { data: printeroptions } = useAppSelector(
    (state) => state.PrinterOptionsSlice
  );

  useEffect(() => {
    dispatch(PrinterOptions());
  }, [dispatch]);

  // Map to convert form field names to category IDs
  const fieldToCategoryMap: Record<string, string> = {
    papper_size: "CAT001",
    side: "CAT002",
    page_orientation: "CAT003",
    scale: "CAT004",
    stapler_position: "CAT006",
    color: "CAT007",
    pages_per_sheet: "CAT008",
  };


  const convertValueToPoId = (value: string, categoryId: string): string => {
    if (!printeroptions?.data || !Array.isArray(printeroptions.data)) return value;
    
    const option = printeroptions.data.find(
      (opt: IPrinterOption) =>
        opt.category_id === categoryId && opt.printer_option_value === value
    );
    
    return option?.po_id || value;
  };

  // Convert initialValues to use po_id instead of printer_option_value
  const convertedInitialValues = React.useMemo(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) {
      return initialValues;
    }

    const converted = { ...initialValues };
    
   
    Object.keys(fieldToCategoryMap).forEach((fieldName) => {
      const categoryId = fieldToCategoryMap[fieldName];
      const fieldValue = converted[fieldName as keyof IPrinter];
      
      if (fieldValue && typeof fieldValue === "string") {
        converted[fieldName as keyof IPrinter] = convertValueToPoId(
          fieldValue,
          categoryId
        ) as any;
      }
    });

    return converted;
  }, [initialValues, printeroptions]);

  const methods = useForm<IPrinter>({
    defaultValues: {
      printer_id: "0",
      printer_name: "",
      model: "",
      status: true,
      papper_size: "",
      side: "",
      page_orientation: "",
      scale: "",
      stapler: false,
      stapler_position: "",
      color: "",
      pages_per_sheet: "",
      is_delete: false,
      ...convertedInitialValues,
    },
    resolver: zodResolver(printerSchema),
    mode: "onChange",
  });

  console.log(initialValues, "inital values");
  console.log(convertedInitialValues, "converted initial values");

  const onSubmit: SubmitHandler<IPrinter> = (values) => {
    const convertedValues = { ...values };
    
    Object.keys(fieldToCategoryMap).forEach((fieldName) => {
      const categoryId = fieldToCategoryMap[fieldName];
      const fieldValue = convertedValues[fieldName as keyof IPrinter];
      
      if (fieldValue && typeof fieldValue === "string") {
        convertedValues[fieldName as keyof IPrinter] = convertValueToPoId(
          fieldValue,
          categoryId
        ) as any;
      }
    });

    const payload: IPrinter = {
      ...convertedValues,
      printer_id: convertedValues.printer_id && convertedValues.printer_id !== "0" 
        ? convertedValues.printer_id   
        : "0",              
    };
    
    console.log(payload, "final payload with po_ids");
    dispatch(printerCreate_Update(payload));
  };

  useEffect(() => {
    if (convertedInitialValues && Object.keys(convertedInitialValues).length > 0) {
      methods.reset(convertedInitialValues);
    }
  }, [convertedInitialValues, methods]);

  useEffect(() => {
    return () => setInitialValues({});
  }, [setInitialValues]);

  const optionsByCategory = React.useMemo((): Record<
    string,
    IPrinterOption[]
  > => {
    if (!printeroptions?.data) return {};
    return Array.isArray(printeroptions.data)
      ? printeroptions.data.reduce(
          (acc: Record<string, IPrinterOption[]>, option: IPrinterOption) => {
            if (!acc[option.category_id]) acc[option.category_id] = [];
            acc[option.category_id].push(option);
            return acc;
          },
          {}
        )
      : {};
  }, [printeroptions]);

  const fieldCategoryMap: Record<
    string,
    { name: keyof IPrinter; label: string }
  > = {
    CAT001: { name: "papper_size", label: "Paper Size" },
    CAT002: { name: "side", label: "Side" },
    CAT003: { name: "page_orientation", label: "Page Orientation" },
    CAT004: { name: "scale", label: "Scale" },
    CAT006: { name: "stapler_position", label: "Stapler Position" },
    CAT007: { name: "color", label: "Color" },
    CAT008: { name: "pages_per_sheet", label: "Pages per Sheet" },
  };

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        {/* Printer Name */}
        <TextInputFieldC name="printer_name" label="Printer Name" type="text" />

        {/* Model */}
        <TextInputFieldC name="model" label="Model" type="text" />

        {/* Status */}
        <Select
          name="status"
          label="Status"
          options={statusOptions}
          value={
            statusOptions.find(
              (option) => option.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            methods.setValue(
              "status",
              (value as AutocompleteOption)?.value ?? true,
              {
                shouldValidate: true,
              }
            );
          }}
        />

        {/* Stapler (boolean) */}
        <Select
          name="stapler"
          label="Stapler"
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
          value={
            methods.getValues("stapler")
              ? { label: "Yes", value: true }
              : { label: "No", value: false }
          }
          onChange={(_, value) => {
            methods.setValue(
              "stapler",
              (value as AutocompleteOption)?.value ?? false,
              {
                shouldValidate: true,
              }
            );
          }}
        />

        {Object.entries(fieldCategoryMap).map(([catId, { name, label }]) => (
          <Select
            key={catId}
            name={name}
            label={label}
            options={
              optionsByCategory[catId]?.map((opt) => ({
                label: opt.printer_option_value,
                value: opt.po_id,
              })) ?? []
            }
            value={
              methods.getValues(name)
                ? {
                    label:
                      optionsByCategory[catId]?.find(
                        (opt) => opt.po_id === methods.getValues(name)
                      )?.printer_option_value ?? String(methods.getValues(name)),
                    value: String(methods.getValues(name)),
                  }
                : null
            }
            onChange={(_, value) => {
              methods.setValue(
                name,
                (value as AutocompleteOption)?.value ?? "",
                {
                  shouldValidate: true,
                }
              );
            }}
          />
        ))}
      </form>
    </FormProvider>
  );
};

export default memo(PrinterForm);